// Phase 24 M4 — Auth API wrappers (password + Google).
//
// Replaces the M1 OTP wrappers. The thin axios layer matches the public
// PRD §"Endpoints" surface. All return TokenPair (access + refresh +
// user + needs_onboarding) so the AuthContext can persist them in one
// step.

import {
  API_VERSION_PREFIX,
  type AuthenticatedUser,
  type GoogleLoginRequest,
  type PasswordLoginRequest,
  type RegisterRequest,
  type ResendVerificationRequest,
  type SetupPasswordRequest,
  type TokenPair,
} from '@leyrochain/shared';

import { apiClient } from './client';

export async function registerUser(input: RegisterRequest): Promise<TokenPair> {
  const { data } = await apiClient.post<TokenPair>(`${API_VERSION_PREFIX}/auth/register`, input);
  return data;
}

export async function loginWithPassword(input: PasswordLoginRequest): Promise<TokenPair> {
  const { data } = await apiClient.post<TokenPair>(`${API_VERSION_PREFIX}/auth/login`, input);
  return data;
}

export async function loginWithGoogle(input: GoogleLoginRequest): Promise<TokenPair> {
  const { data } = await apiClient.post<TokenPair>(`${API_VERSION_PREFIX}/auth/google`, input);
  return data;
}

export async function refreshSession(refresh_token: string): Promise<TokenPair> {
  const { data } = await apiClient.post<TokenPair>(`${API_VERSION_PREFIX}/auth/refresh`, {
    refresh_token,
  });
  return data;
}

export async function logoutSession(refresh_token: string): Promise<void> {
  await apiClient.post(`${API_VERSION_PREFIX}/auth/logout`, { refresh_token });
}

export async function verifyEmail(token: string): Promise<{ ok: true; email: string }> {
  const { data } = await apiClient.get<{ ok: true; email: string }>(
    `${API_VERSION_PREFIX}/auth/verify-email`,
    { params: { token } },
  );
  return data;
}

export async function resendVerification(input: ResendVerificationRequest): Promise<void> {
  await apiClient.post(`${API_VERSION_PREFIX}/auth/resend-verification`, input);
}

export async function setupPassword(input: SetupPasswordRequest): Promise<TokenPair> {
  const { data } = await apiClient.post<TokenPair>(
    `${API_VERSION_PREFIX}/auth/setup-password`,
    input,
  );
  return data;
}

export async function fetchMe(): Promise<AuthenticatedUser> {
  const { data } = await apiClient.get<AuthenticatedUser>(`${API_VERSION_PREFIX}/auth/me`);
  return data;
}
